import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

type Transaction = {
  amount: number;
  reason: string;
  details: string;
  created_at: string;
};

export default function CreditHistory() {
  const { token, user, refreshUser } = useAuth();
  const [history, setHistory] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Set page title
  useEffect(() => {
    document.title = "Credit History • BacklinkBanana";
  }, []);

  // Refresh user (to sync credits) and fetch history
  useEffect(() => {
    refreshUser();

    const fetchHistory = async () => {
      setLoading(true);
      setError(null);

      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/credit-history`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (!res.ok) throw new Error("Failed to load credit history.");
        const data: Transaction[] = await res.json();
        setHistory(data);
      } catch (err: any) {
        setError(err.message || "Something went wrong");
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [token, refreshUser]);

  return (
    <div className="max-w-3xl mx-auto mt-12 px-4">
      <h1 className="text-2xl font-bold mb-6 text-blue-800">
        Credit History
      </h1>

      {/* Top-up prompt if out of credits */}
      {user?.credits !== undefined && user.credits <= 0 && (
        <div className="mb-6 p-4 bg-yellow-100 rounded-lg text-center">
          <span className="font-semibold text-yellow-800">
            You’re out of credits!
          </span>{" "}
          <Link
            to="/buy-credits"
            className="underline text-yellow-900 ml-1"
          >
            Buy more credits
          </Link>
        </div>
      )}

      {loading && <p>Loading...</p>}
      {error && <p className="text-red-500">{error}</p>}
      {!loading && !error && history.length === 0 && (
        <p>No credit activity found.</p>
      )}

      {history.length > 0 && (
        <div className="rounded-lg shadow overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-200">
            <thead className="bg-blue-100 text-blue-700">
              <tr>
                <th className="px-4 py-2 text-left">Date</th>
                <th className="px-4 py-2 text-left">Reason</th>
                <th className="px-4 py-2 text-left">Details</th>
                <th className="px-4 py-2 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {history.map((tx, idx) => (
                <tr key={`${tx.created_at}-${idx}`} className="border-t">
                  <td className="px-4 py-2">
                    {new Date(tx.created_at).toLocaleString()}
                  </td>
                  <td className="px-4 py-2 capitalize">{tx.reason}</td>
                  <td className="px-4 py-2 text-sm">{tx.details || "—"}</td>
                  <td
                    className={`px-4 py-2 text-right font-semibold ${
                      tx.amount < 0 ? "text-red-600" : "text-green-600"
                    }`}
                  >
                    {tx.amount > 0 ? `+${tx.amount}` : tx.amount}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
